import { useEffect, useMemo } from 'react';
import { Outlet, useNavigate } from '@tanstack/react-router';
import {
  Box,
  Flex,
  Page,
  PageBody,
  PageHeader,
  PageHeaderActions,
  PageHeaderIconTile,
  PageHeaderTabs,
  PageHeaderTitle,
  PageLoader,
  Pagination,
  TabNav,
} from '@customerio/pluma-components/react';
import { RumPageRendered } from '@/services/rum/use-page-rendered';
import { BroadcastsListContext } from './list-context';
import { CreateBroadcastButton } from './components/create-broadcast-button';
import { BroadcastsWelcome } from './components/broadcasts-welcome';
import { BroadcastsSearch } from './components/broadcasts-search';
import { BROADCASTS_PAGE_SIZE, withoutDefaults } from './url-state';
import { BROADCASTS_TAB_ROUTES, rememberBroadcastsTab } from './tabs';
import { useBroadcastsList } from './use-broadcasts-list';
import { useListViewedAnalytics } from './use-list-viewed-analytics';
import * as styles from './styles.css';

/**
 * The Broadcasts list: its header, the search box and tab bar both tabs share, pagination
 * above and below, and the showing tab's table in between. A workspace with no broadcasts of
 * either kind gets the welcome state instead.
 */
export default function BroadcastsListPage() {
  const list = useBroadcastsList();
  const navigate = useNavigate();
  useListViewedAnalytics(list);

  const { workspaceId, activeTab, activeSearch, saved, newslettersQuery, apiTriggeredQuery } = list;
  const context = useMemo(
    () => ({ saved, newslettersQuery, apiTriggeredQuery }),
    [saved, newslettersQuery, apiTriggeredQuery],
  );

  // A bare `/broadcasts` returns to the last tab shown, even after leaving the page.
  useEffect(() => {
    if (activeTab != null) {
      rememberBroadcastsTab(activeTab);
    }
  }, [activeTab]);

  const goTo = (search: typeof activeSearch, { replace }: { replace: boolean }) => {
    if (activeTab == null || search == null) {
      return;
    }
    void navigate({
      to: BROADCASTS_TAB_ROUTES[activeTab],
      params: { workspaceId: String(workspaceId) },
      search: withoutDefaults(search),
      replace,
    });
  };

  // A bare `/broadcasts` renders only its redirect to a tab, which must not wait on the reads.
  if (activeTab == null) {
    return (
      <BroadcastsListContext.Provider value={context}>
        <Outlet />
      </BroadcastsListContext.Provider>
    );
  }

  if (list.isLoading) {
    return <PageLoader />;
  }

  if (list.shouldDisplayWelcome) {
    return (
      <RumPageRendered>
        <Box mt="400">
          <BroadcastsWelcome workspaceId={workspaceId} tab={activeTab} />
        </Box>
      </RumPageRendered>
    );
  }

  // Searching starts over on the first page, and replaces the history entry rather than
  // adding one per search. Turning the page adds one, so Back returns to the previous page.
  const onSearch = (value: string) => goTo({ ...activeSearch, search: value, page: undefined }, { replace: true });
  const onPageChange = (page: number) => goTo({ ...activeSearch, page }, { replace: false });

  const pagination = (
    <Pagination
      totalItemCount={list.total}
      pageSize={BROADCASTS_PAGE_SIZE}
      page={list.page}
      onPageChange={onPageChange}
    />
  );

  return (
    <BroadcastsListContext.Provider value={context}>
      <Page>
        <PageHeader>
          <PageHeaderIconTile name="broadcast" />
          <PageHeaderTitle>Broadcasts</PageHeaderTitle>
          <PageHeaderActions>
            <CreateBroadcastButton workspaceId={workspaceId} />
          </PageHeaderActions>
          <PageHeaderTabs mt="100">
            <Box px="300" mb="200">
              {/* Keyed on the applied search, so a tab link or Back that changes it resets the field. */}
              <BroadcastsSearch key={list.search} value={list.search} onSearch={onSearch} />
            </Box>
            <Flex justifyContent="space-between" w="full" pr="300" className={styles.tabsRow}>
              <TabNav
                aria-label="Broadcasts"
                tabs={list.tabs}
                variant="default"
                isBorderless
                tabsListAlignInline="flex-start"
              />
              <Box mb="100">{pagination}</Box>
            </Flex>
          </PageHeaderTabs>
        </PageHeader>
        <PageBody>
          <Outlet />
          {list.hasRows ? <Flex justifyContent="flex-end">{pagination}</Flex> : null}
        </PageBody>
      </Page>
    </BroadcastsListContext.Provider>
  );
}
